import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { queryKeyMap as projectQueryKeyMap } from '@/features/project/_utils/query-keys';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import {
  type BudgetTypeFormSchema,
  BudgetTypeModel,
  BudgetTypeMutationParamsSchema,
  BudgetTypesModel,
  type UpdateBudgetTypeSortOrderParamsSchema,
} from '../models';

type BudgetTypeParams = {
  fiscalYear?: string;
};

const budgetTypeUrl = '/api/budget-types/';

const _useBudgetType = ({
  id,
}: {
  id: string;
}) => {
  return useQuery({
    queryKey: ['budgetType', id],
    queryFn: async () => {
      const budgetTypes = await axiosGet(`${budgetTypeUrl}${id}/`);
      return BudgetTypeModel.parse(budgetTypes);
    },
    enabled: !!id,
  });
};

export const useBudgetTypes = <U = z.infer<typeof BudgetTypesModel>>(
  props: {
    params?: BudgetTypeParams;
    select?: (data: z.infer<typeof BudgetTypesModel>) => U;
    enabled?: boolean;
  } = {
    enabled: true,
  },
) => {
  const { params, select, enabled } = props;
  return useQuery({
    queryKey: ['budgetTypes', params],
    queryFn: async () => {
      const budgetTypes = await axiosGet(budgetTypeUrl, params);
      return BudgetTypesModel.parse(budgetTypes);
    },
    select,
    enabled,
  });
};

type FormValues = z.infer<typeof BudgetTypeFormSchema>;

export const useCreateBudgetType = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async ({ scheduledDateOfDecision, ...values }: FormValues) => {
      const payload = BudgetTypeMutationParamsSchema.parse({
        ...values,
        fiscalYear: values.fiscalYear.year().toString(),
        scheduledDateOfDecision: scheduledDateOfDecision
          ? scheduledDateOfDecision.format('YYYY-MM-DD')
          : null,
      });
      const res = await axiosPost(budgetTypeUrl, payload);
      await Promise.all([
        invalidate(['budgetTypes']),
        invalidate(['baseData']),
      ]);
      return res;
    },
  });
};

export const useUpdateBudgetType = () => {
  return useMutation({
    mutationFn: async ({
      id,
      scheduledDateOfDecision,
      ...values
    }: FormValues & { id: string }) => {
      const payload = BudgetTypeMutationParamsSchema.parse({
        ...values,
        fiscalYear: values.fiscalYear.year().toString(),
        scheduledDateOfDecision: scheduledDateOfDecision
          ? scheduledDateOfDecision.format('YYYY-MM-DD')
          : null,
      });
      return await axiosPatch(`${budgetTypeUrl}${id}/`, payload);
    },
  });
};

export const useDeleteBudgetType = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`${budgetTypeUrl}${id}/`);
      await invalidate(['budgetTypes']);
      return res;
    },
  });
};

export const useChangeOrderOfBudgetType = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      params: z.infer<typeof UpdateBudgetTypeSortOrderParamsSchema>,
    ) => {
      const res = await axiosPost(`${budgetTypeUrl}update-sort-order/`, params);
      await Promise.all([
        invalidate(['budgetTypes']),
        // SubProject's subProjectBudgetTypeAttributes will be changed when the order of budgetTypes is changed.
        invalidate(projectQueryKeyMap.subProjects()),
        invalidate(projectQueryKeyMap.project()),
      ]);
      return res;
    },
  });
};
